
import Vue from "vue";
import { mapState } from "vuex";
import { IRootState } from "@/store/types";
import { ReferenceDataType } from "@/store/referenceData/types";
import { REFERENCE_DATA } from "@/store/referenceData";
import { STANDARD_OFFER } from "@/store/standardOffer";
import { IStandardOfferDto } from "@/store/standardOffer/types";
import {
  alphaNum,
  maxLength,
  required,
  requiredIf,
  decimal,
  integer,
  numeric,
  or
} from "vuelidate/lib/validators";
import { numberGreaterThan } from "@/validators";
import { addYears } from "date-fns";
import { ROUTE_NAMES } from "@/routes";
import { isInSomeRole } from "@/helpers/auth";
import { SUPPORT, PRICING_TEAM } from "@/constants/roles";

const MAX_DATE_VALUE_YEARS = 5;

export default Vue.extend({
  props: {
    id: {
      // Comes through as a number from the router
      // And a string if you hit the URL directly
      type: [Number, String],
      required: false
    }
  },
  data() {
    return {
      ReferenceDataType,
      minimumAmountErrorMessages: [],
      maximumAmountErrorMessages: []
    };
  },
  watch: {
    id: {
      immediate: true,
      handler(value) {
        this.$store.dispatch(
          STANDARD_OFFER.NS(STANDARD_OFFER.ACTIONS.RESET_STATE)
        );

        this.fetchData();
      }
    }
  },
  computed: {
    hasEditAccess() {
      return isInSomeRole([PRICING_TEAM, SUPPORT]);
    },
    isDisabled() {
      return !this.hasEditAccess || this.isLoading || this.uiIsLoading;
    },
    listRouteName: () => ROUTE_NAMES.STANDARD_OFFERS,
    ...mapState({
      isLoading: (state: IRootState) => state.standardOffer.isLoading,
      uiIsLoading: (state: IRootState) => state.ui.isLoading
    }),
    deliveryMethod: {
      get(): string {
        return this.model.deliveryMethod;
      },
      set(value: string) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_DELIVERY_METHOD),
          value
        );
      }
    },
    minDateValue(): Date | undefined {
      // The min date for updates should not be restricted to todays date.
      if (this.id && this.model) {
        return undefined;
      }

      return new Date();
    },
    maxDateValue(): Date {
      return addYears(new Date(), MAX_DATE_VALUE_YEARS);
    },
    model(): IStandardOfferDto {
      return this.$store.state.standardOffer.offer;
    },
    name: {
      get(): any {
        return this.model.name;
      },
      set(value: string) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_NAME),
          value
        );
      }
    },
    startDate: {
      get(): any {
        return this.model.validFromDate;
      },
      set(value: Date) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_VALID_FROM_DATE),
          value
        );
      }
    },
    minimumAmount: {
      get(): number {
        return this.model.minimumAmount;
      },
      set(value: number) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_MINIMUM_AMOUNT),
          value
        );
      }
    },
    maximumAmount: {
      get(): number {
        return this.model.maximumAmount;
      },
      set(value: number) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_MAXIMUM_AMOUNT),
          value
        );
      }
    },
    discounts: {
      get(): any {
        return this.model.discounts;
      },
      set(value: any) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_DISCOUNTS),
          value
        );
      }
    },
    applicationType: {
      get(): any {
        return this.model.applicationType;
      },
      set(value: string) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_APPLICATION_TYPE),
          value
        );
      }
    },
    customerType: {
      get(): any {
        return this.model.customerType;
      },
      set(value: string) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_CUSTOMER_TYPE),
          value
        );
      }
    },
    classOfBuyer: {
      get(): any {
        return this.model.classOfBuyer;
      },
      set(value: string) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_CLASS_OF_BUYER),
          value
        );
      }
    },
    pricingProfile: {
      get(): any {
        return this.model.pricingProfile;
      },
      set(value: string) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_PRICING_PROFILE),
          value
        );
      }
    },
    rebateProfile: {
      get(): any {
        return this.model.rebateProfile;
      },
      set(value: string[]) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_REBATE_PROFILE),
          value
        );
      }
    },
    salesRepresentative: {
      get(): any {
        return this.model.salesRepresentative;
      },
      set(value: string) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_SALES_REPRESENTATIVE),
          value
        );
      }
    },
    collectionManager: {
      get(): any {
        return this.model.collectionManager;
      },
      set(value: string) {
        this.$store.commit(
          STANDARD_OFFER.NS(STANDARD_OFFER.MUTATIONS.SET_COLLECTION_MANAGER),
          value
        );
      }
    }
  },
  methods: {
    validateAmounts() {
      this.validateMinimumAmount();
      this.validateMaximumAmount();
    },
    validateMinimumAmount() {
      this.minimumAmountErrorMessages = [
        this.$v.minimumAmount.required || "This field is required",
        this.$v.minimumAmount.integer || "This field must be a whole number"
      ].filter(x => x !== true);
      this.$v.minimumAmount.$touch();
    },
    validateMaximumAmount() {
      this.maximumAmountErrorMessages = [
        this.$v.maximumAmount.required || "This field is required",
        this.$v.maximumAmount.integer || "This field must be a whole number",
        this.$v.maximumAmount.greaterThan ||
        "Must be greater than Spending band from"
      ].filter(x => x !== true);
      this.$v.maximumAmount.$touch();
    },
    fetchData() {
      this.$store.dispatch(
        REFERENCE_DATA.NS(REFERENCE_DATA.ACTIONS.FETCH_REFERENCE_DATA)
      );

      if (this.id) {
        this.$store.dispatch(
          STANDARD_OFFER.NS(
            STANDARD_OFFER.ACTIONS.LOAD_STANDARD_OFFER_BY_OFFER_ID
          ),
          this.id
        );
      }
    },
    async onSubmit() {
      if (this.id && this.id !== "") {
        await this.$store.dispatch(
          STANDARD_OFFER.NS(STANDARD_OFFER.ACTIONS.UPDATE_STANDARD_OFFER),
          this.id
        );
      } else {
        await this.$store.dispatch(
          STANDARD_OFFER.NS(STANDARD_OFFER.ACTIONS.CREATE_STANDARD_OFFER),
          // onSuccess callback
          () => this.$router.push({ name: ROUTE_NAMES.STANDARD_OFFERS })
        );
      }
    }
  },
  validations() {
    return {
      name: {
        required,
        maxLength: maxLength(50)
      },
      startDate: {
        required
      },
      minimumAmount: {
        required,
        integer
      },
      maximumAmount: {
        required,
        integer,
        greaterThan: numberGreaterThan((vm: any) => vm.minimumAmount)
      },
      applicationType: {
        required
      },
      customerType: {
        required
      },
      classOfBuyer: {
        required
      },
      pricingProfile: {
        required
      },
      rebateProfile: {
        required
      },
      salesRepresentative: {
        required
      },
      collectionManager: {
        required
      }
    };
  }
});
